/**
 * Painted-vs-layout scale of an element: cumulative CSS `zoom` times any ancestor
 * `transform: scale()`.
 *
 * Report pages shrink #media-container with `zoom` (maneuver MAP / VIDEO, perf charts) or with
 * `transform: scale()` (everything else) — see setupMediaContainerScaling in utils/global.ts. Anything that
 * mixes painted measurements (getBoundingClientRect) with layout measurements (scrollTop,
 * clientHeight) has to divide the painted ones by this factor, or it is off by exactly the page
 * scale.
 *
 * Used by the sidebar chart search (utils/chartSearchTargets.ts) to convert painted rects to layout px.
 */

const transformScaleX = (value: string): number => {
  if (!value || value === "none") {return 1;}
  try {
    const a = new DOMMatrixReadOnly(value).a;
    return Number.isFinite(a) && a > 0 ? a : 1;
  } catch {
    return 1;
  }
};

export const getEffectiveScale = (el: HTMLElement): number => {
  let scale = 1;

  const currentZoom = (el as HTMLElement & { currentCSSZoom?: number }).currentCSSZoom;
  if (typeof currentZoom === "number" && currentZoom > 0) {
    scale = currentZoom;
  } else {
    for (let node: HTMLElement | null = el; node; node = node.parentElement) {
      const zoom = parseFloat(window.getComputedStyle(node).zoom);
      if (Number.isFinite(zoom) && zoom > 0) {
        scale *= zoom;
      }
    }
  }

  for (let node: HTMLElement | null = el; node; node = node.parentElement) {
    scale *= transformScaleX(window.getComputedStyle(node).transform);
  }

  return Number.isFinite(scale) && scale > 0 ? scale : 1;
};

export { transformScaleX };
